import {resolveTradeProfitLoss, TradeStatus, type Trade} from '@cp/model';
import {ChevronLeft, ChevronRight, Delete, Edit, ExpandMore, Refresh, Save} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {getDownloadURL, ref as storageRef} from 'firebase/storage';
import {useEffect, useState} from 'react';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import TradeForm from '../../../components/TradeForm/TradeForm';
import {env} from '../../../config/env';
import {firebaseStorage} from '../../../config/firebase';
import type {EditableTrade} from './AdminTradeTypes';

function formatTradeEpochMs(ms: number | undefined): string {
  if (ms === undefined || !Number.isFinite(ms) || ms <= 0) return '—';
  return dayjs(ms).toISOString();
}

function formatExitPriceForDisplay(exitPrice: number | undefined): string | number {
  return exitPrice !== undefined && Number.isFinite(exitPrice) && exitPrice > 0 ? exitPrice : '—';
}

function tradePositionSizeForPnL(trade: Trade): number {
  return trade.positionSize !== undefined && Number.isFinite(trade.positionSize) && trade.positionSize > 0
    ? trade.positionSize
    : 1;
}

function calculateTradePnL(trade: Trade): number | undefined {
  return resolveTradeProfitLoss({...trade, positionSize: tradePositionSizeForPnL(trade)});
}

function TradeMessageImage({screenshotStoragePath}: {screenshotStoragePath?: unknown}) {
  const [resolvedUrl, setResolvedUrl] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!screenshotStoragePath) {
      setResolvedUrl(undefined);
      return;
    }

    const resolvedStoragePath =
      typeof screenshotStoragePath === 'string' && screenshotStoragePath.length > 0 ? screenshotStoragePath : undefined;

    if (!resolvedStoragePath) {
      setResolvedUrl(undefined);
      return;
    }

    // If it's already a URL, we can render directly.
    if (
      resolvedStoragePath.startsWith('http://') ||
      resolvedStoragePath.startsWith('https://') ||
      resolvedStoragePath.startsWith('data:')
    ) {
      setResolvedUrl(resolvedStoragePath);
      return;
    }

    // Otherwise, assume it's a Firebase Storage path and resolve to a download URL.
    const run = async () => {
      try {
        setLoading(true);
        const url = await getDownloadURL(storageRef(firebaseStorage, resolvedStoragePath));
        setResolvedUrl(url);
      } catch (err) {
        console.error('Failed to resolve screenshot URL', err);
        setResolvedUrl(undefined);
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [screenshotStoragePath]);

  if (!screenshotStoragePath) return null;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 120,
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!resolvedUrl) return null;

  return (
    <Box
      component="img"
      src={resolvedUrl}
      alt="Trade message"
      sx={{
        maxWidth: '100%',
        maxHeight: 360,
        objectFit: 'contain',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    />
  );
}

interface AdminTradeTradesProps {
  trades: EditableTrade[];
  loading: boolean;
  pageDepth: number;
  hasNextPage: boolean;

  savingId: string | undefined;

  deleteDialogOpen: boolean;
  deleteDialogLoading: boolean;

  onReloadTrades: () => Promise<void>;
  onTradesPrev: () => void;
  onTradesNext: () => void;

  onEditToggle: (id: string, editing: boolean) => void;
  onFieldChange: <K extends keyof EditableTrade>(id: string, field: K, value: EditableTrade[K]) => void;
  onSaveTrade: (trade: EditableTrade) => void | Promise<void>;

  onRequestDelete: (id: string) => void;
  onDeleteDialogConfirm: () => void;
  onDeleteDialogCancel: () => void;
}

export default function AdminTradeTrades({
  trades,
  loading,
  pageDepth,
  hasNextPage,
  savingId,
  deleteDialogOpen,
  deleteDialogLoading,
  onReloadTrades,
  onTradesPrev,
  onTradesNext,
  onEditToggle,
  onFieldChange,
  onSaveTrade,
  onRequestDelete,
  onDeleteDialogConfirm,
  onDeleteDialogCancel,
}: AdminTradeTradesProps) {
  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{mb: 2}}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={() => onReloadTrades()}
          disabled={loading}
        >
          Reload
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ChevronLeft />}
          onClick={() => onTradesPrev()}
          disabled={loading || pageDepth === 0}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          size="small"
          endIcon={<ChevronRight />}
          onClick={() => onTradesNext()}
          disabled={loading || !hasNextPage}
        >
          Next
        </Button>
        {loading ? (
          <CircularProgress size={22} sx={{ml: 0.5}} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ml: 0.5}}>
            {trades.length === 0 ? 'No trades on this page' : `${trades.length} trade${trades.length === 1 ? '' : 's'}`}
          </Typography>
        )}
      </Stack>

      <Stack spacing={2.5}>
        {trades.map((trade) => {
          const pnl = calculateTradePnL(trade);
          const pnlText = pnl === undefined ? '—' : `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}`;
          const pnlColor = pnl === undefined ? 'text.secondary' : pnl >= 0 ? 'success.main' : 'error.main';

          return (
            <Accordion key={trade.id} disableGutters>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`trade-${trade.id}-content`}
                id={`trade-${trade.id}-header`}
                sx={{
                  py: 2,
                  px: {xs: 1.5, sm: 2},
                  '& .MuiAccordionSummary-content': {alignItems: 'stretch', my: 0.5},
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    width: '100%',
                    gap: {xs: 2, md: 3},
                    pr: 1,
                    alignItems: 'start',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr) auto',
                    },
                  }}
                >
                  <Stack spacing={1.25} sx={{minWidth: 0}}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {trade.symbol}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={env.trade.direction[trade.direction].label}
                        color={env.trade.direction[trade.direction].color}
                        variant="filled"
                      />
                      <Chip
                        size="small"
                        label={env.trade.status[trade.status].label}
                        color={env.trade.status[trade.status].color}
                        variant={trade.status === TradeStatus.CLOSED ? 'outlined' : 'filled'}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.6}}>
                      Session: {trade.sessionId ?? '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.6}}>
                      Trade # (replay/broker): {trade.tradeId?.trim() ? trade.tradeId : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.5}}>
                      Doc id: {trade.id}
                    </Typography>
                  </Stack>

                  <Stack spacing={1} sx={{minWidth: 0}}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                      Opened
                    </Typography>
                    <Typography variant="body2">{formatTradeEpochMs(trade.openedAt)}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      textTransform="uppercase"
                      sx={{mt: 1}}
                    >
                      Entry / exit
                    </Typography>
                    <Typography variant="body2">
                      {Number.isFinite(trade.entryPrice) ? trade.entryPrice : '—'} /{' '}
                      {formatExitPriceForDisplay(trade.exitPrice)}
                    </Typography>
                    {trade.positionSize !== undefined && Number.isFinite(trade.positionSize) && (
                      <>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          textTransform="uppercase"
                          sx={{mt: 1}}
                        >
                          Size
                        </Typography>
                        <Typography variant="body2">{trade.positionSize}</Typography>
                      </>
                    )}
                  </Stack>

                  <Stack spacing={0.75} sx={{minWidth: 0, alignItems: {xs: 'flex-start', md: 'flex-end'}}}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                      P/L
                    </Typography>
                    <Typography variant="h6" sx={{color: pnlColor, fontWeight: 700}}>
                      {pnlText}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()} sx={{justifySelf: 'end'}}>
                    {trade.isEditing ? (
                      <IconButton
                        component="span"
                        aria-label="Save"
                        onClick={(e) => {
                          e.stopPropagation();
                          void onSaveTrade(trade);
                        }}
                        disabled={savingId === trade.id}
                        size="small"
                      >
                        <Save fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton
                        component="span"
                        aria-label="Edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditToggle(trade.id, true);
                        }}
                        size="small"
                        disabled={savingId !== undefined}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}

                    <IconButton
                      component="span"
                      aria-label="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRequestDelete(trade.id);
                      }}
                      size="small"
                      disabled={savingId === trade.id}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={2}>
                  {trade.isEditing ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 2,
                        width: '100%',
                        gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))'},
                        alignItems: 'start',
                      }}
                    >
                      <TradeForm
                        data={trade}
                        setData={(next) => {
                          if (next.symbol !== trade.symbol) onFieldChange(trade.id, 'symbol', next.symbol);
                          if (next.sessionId !== trade.sessionId) onFieldChange(trade.id, 'sessionId', next.sessionId);
                          if (next.tradeId !== trade.tradeId) onFieldChange(trade.id, 'tradeId', next.tradeId);
                          if (next.direction !== trade.direction) onFieldChange(trade.id, 'direction', next.direction);
                          if (next.status !== trade.status) onFieldChange(trade.id, 'status', next.status);
                          if (next.entryPrice !== trade.entryPrice)
                            onFieldChange(trade.id, 'entryPrice', next.entryPrice);
                          if (next.exitPrice !== trade.exitPrice) onFieldChange(trade.id, 'exitPrice', next.exitPrice);
                          if (next.profitLoss !== trade.profitLoss)
                            onFieldChange(trade.id, 'profitLoss', next.profitLoss);
                          if (next.closedAt !== trade.closedAt) onFieldChange(trade.id, 'closedAt', next.closedAt);
                          if (next.tags !== trade.tags) onFieldChange(trade.id, 'tags', next.tags);
                        }}
                      />
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      <Stack direction={{xs: 'column', sm: 'row'}} spacing={{xs: 1, sm: 4}} flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" color="text.secondary">
                          Opened at: {formatTradeEpochMs(trade.openedAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Entry: {Number.isFinite(trade.entryPrice) ? trade.entryPrice : '—'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Exit: {formatExitPriceForDisplay(trade.exitPrice)}
                        </Typography>
                        {trade.positionSize !== undefined && Number.isFinite(trade.positionSize) && (
                          <Typography variant="body2" color="text.secondary">
                            Size: {trade.positionSize}
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Trade id: {trade.tradeId?.trim() ? trade.tradeId : '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tags: {(trade.tags ?? []).join(', ') || '—'}
                      </Typography>
                    </Stack>
                  )}

                  <Divider />

                  <Stack spacing={1}>
                    <Typography variant="subtitle1">Messages</Typography>
                    {trade.messages.map((msg, index) => (
                      <Box
                        key={
                          msg.createdAt !== undefined && Number.isFinite(msg.createdAt)
                            ? `${trade.id}-msg-${msg.createdAt}-${index}`
                            : `${trade.id}-${index}`
                        }
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 2,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {formatTradeEpochMs(msg.createdAt)}
                        </Typography>
                        <Typography variant="body2" sx={{mt: 0.5}}>
                          {msg.note?.trim() ? msg.note : '—'}
                        </Typography>
                        <Box sx={{mt: 1}}>
                          <TradeMessageImage screenshotStoragePath={msg.screenshotStoragePath} />
                        </Box>
                      </Box>
                    ))}
                    {trade.messages.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No messages yet.
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {trades.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
            No trades yet.
          </Typography>
        )}
      </Stack>

      <ConfirmationDialog
        title="Delete trade"
        content="Are you sure you want to delete this trade?"
        confirmButtonLabel="Delete"
        confirmButtonColor="error"
        open={deleteDialogOpen}
        loading={deleteDialogLoading}
        handleConfirm={onDeleteDialogConfirm}
        handleCancel={onDeleteDialogCancel}
        onClose={onDeleteDialogCancel}
      />
    </>
  );
}
